import { Scene } from "@ideastatica/scene/build/Types";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ConDesignItemLite, ConDesignItemWithPicture, PaginatedCDIsLite } from "../types/ConDesignItem";
import { FeatureClauses, MemberFilter, PopularityFilters } from "../types/CDIFilter";
import { TranslationsRecord } from "../types/TranslationsRecord";
import { User, UserInformations } from "../types/User";
import { SceneUrl } from "../types/SceneUrl";
import { SeqData } from "../types/SeqData";

interface FiltersLite {
	paginationFrom: number;
	searchQueryString: string;
}

interface FetchIdeaConPublicUrlQueryParams {
	itemId: string;
	loggingPurpose: string;
}

interface IdeaConProjectPublicUrlResponse {
	url: string;
	wasDownloadCountIncremented: boolean;
}

// Global callback for bookmark removal - this allows components to listen for bookmark changes
let globalBookmarkRemovedCallback: ((itemId: string) => void) | null = null;

export const setBookmarkRemovedCallback = (callback: (itemId: string) => void) => {
	globalBookmarkRemovedCallback = callback;
};

export const clearBookmarkRemovedCallback = () => {
	globalBookmarkRemovedCallback = null;
};

export const clApiSlice = createApi({
	reducerPath: "api",
	baseQuery: fetchBaseQuery({
		credentials: "include",
	}),
	tagTypes: ["Post", "Get", "DesignItem", "Bookmark", "BookmarksList"],
	endpoints: (builder) => ({
		// Bookmark an item
		bookmarkDesignItem: builder.mutation<{ success: boolean; message: string }, string>({
			query: (designItemId) => ({
				url: `/Home/BookmarkConnection?designItemId=${designItemId}`,
				method: "POST",
			}),
			onQueryStarted: async (designItemId, { dispatch, queryFulfilled, getState }) => {
				const detailPatch = dispatch(
					clApiSlice.util.updateQueryData("fetchDesignItem", designItemId, (draft) => {
						draft.isBookmarked = true;
						if (draft.designItem && draft.designItem.bookmarkCount !== null) {
							draft.designItem.bookmarkCount = (draft.designItem.bookmarkCount || 0) + 1;
						}
					}),
				);
				
				const state = getState() as any;
				const detailQuery = state.api.queries[`fetchDesignItem("${designItemId}")`];
				const patches: any[] = [];
				
				if (detailQuery?.data) {
					const itemToAdd: ConDesignItemLite = {
						id: detailQuery.data.designItem.conDesignItemId,
						description: detailQuery.data.designItem.name || "",
						pictureUrl: detailQuery.data.pictureUrl || "",
					};
					
					Object.entries(state.api.queries).forEach(([key, value]: [string, any]) => {
						if (key.startsWith("fetchDesignItemsLiteAnonymous") && value?.data?.designItems) {
							const args = value.originalArgs;
							if (args?.searchQueryString) {
								try {
									const decodedQuery = JSON.parse(decodeURIComponent(args.searchQueryString));
									
									const popularityFilters = decodedQuery.popularityFilters as PopularityFilters | undefined;
									const hasMyBookmarksFilter = popularityFilters?.MyBookmarks === FeatureClauses.Must;
									
									if (hasMyBookmarksFilter) {
										const patch = dispatch(
											clApiSlice.util.updateQueryData(
												"fetchDesignItemsLiteAnonymous",
												args,
												(draft) => {
													const exists = draft.designItems.some(
														(item) => item.id === designItemId,
													);
													
													if (!exists) {
														draft.designItems.unshift(itemToAdd);
														
														if (typeof draft.totalItems === "number") {
															draft.totalItems += 1;
														}
													}
												},
											),
										);
										patches.push(patch);
									}
								} catch (e) {
									console.error("Error parsing search query:", e);
								}
							}
						}
					});
				}
				
				try {
					await queryFulfilled;
				} catch {
					detailPatch.undo();
					patches.forEach(patch => patch.undo());
				}
			},
			invalidatesTags: (result, error, designItemId) => [
				{ type: "Bookmark", id: designItemId },
			],
		}),
		
		// Unbookmark an item
		unbookmarkDesignItem: builder.mutation<{ success: boolean; message: string }, string>({
			query: (designItemId) => ({
				url: `/Home/UnbookmarkConnection?designItemId=${designItemId}`,
				method: "DELETE",
			}),
			onQueryStarted: async (designItemId, { dispatch, queryFulfilled, getState }) => {
				const detailPatch = dispatch(
					clApiSlice.util.updateQueryData("fetchDesignItem", designItemId, (draft) => {
						draft.isBookmarked = false;
						if (draft.designItem && draft.designItem.bookmarkCount !== null && draft.designItem.bookmarkCount > 0) {
							draft.designItem.bookmarkCount -= 1;
						}
					}),
				);
				
				const state = getState() as any;
				const patches: any[] = [];
				
				Object.entries(state.api.queries).forEach(([key, value]: [string, any]) => {
					if (key.startsWith("fetchDesignItemsLiteAnonymous") && value?.data?.designItems) {
						const args = value.originalArgs;
						if (args?.searchQueryString) {
							try {
								const decodedQuery = JSON.parse(decodeURIComponent(args.searchQueryString));
								
								const popularityFilters = decodedQuery.popularityFilters as PopularityFilters | undefined;
								const hasMyBookmarksFilter = popularityFilters?.MyBookmarks === FeatureClauses.Must;
								
								if (hasMyBookmarksFilter) {
									const patch = dispatch(
										clApiSlice.util.updateQueryData(
											"fetchDesignItemsLiteAnonymous",
											args,
											(draft) => {
												draft.designItems = draft.designItems.filter(
													(item) => item.id !== designItemId,
												);
												
												if (typeof draft.totalItems === "number" && draft.totalItems > 0) {
													draft.totalItems -= 1;
												}
											},
										),
									);
									patches.push(patch);
								}
							} catch (e) {
								console.error("Error parsing search query:", e);
							}
						}
					}
				});
				
				try {
					await queryFulfilled;
					if (globalBookmarkRemovedCallback) {
						globalBookmarkRemovedCallback(designItemId);
					}
				} catch {
					detailPatch.undo();
					patches.forEach(patch => patch.undo());
				}
			},
			invalidatesTags: (result, error, designItemId) => [
				{ type: "Bookmark", id: designItemId },
			],
		}),
		
		fetchIdeaConPublicUrl: builder.query<IdeaConProjectPublicUrlResponse, FetchIdeaConPublicUrlQueryParams>({
			query(params) {
				return `/Home/GetIdeaConProjectPublicUrl?designItemId=${params.itemId}&&loggingPurpose=${params.loggingPurpose}`;
			},
			onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
				if (params.loggingPurpose === "ItemDownload" || params.loggingPurpose === "OpenInDesktop") {
					const { data } = await queryFulfilled;
					
					// Only update cache if the backend actually incremented the count
					if (data.wasDownloadCountIncremented) {
						dispatch(
							clApiSlice.util.updateQueryData("fetchDesignItem", params.itemId, (draft) => {
								if (draft?.designItem) {
									draft.designItem.downloadCount = (draft.designItem.downloadCount ?? 0) + 1;
								}
							}),
						);
					}
				}
			},
		}),
		
		fetchSeqData: builder.query<SeqData, boolean>({
			query() {
				return `/Home/GetSeqData`;
			},
		}),
		
		fetchDesignItemsLiteAnonymous: builder.query<PaginatedCDIsLite, FiltersLite>({
			query(filters) {
				return {
					url: `/Home/GetDesignItemsWithPicturesLiteAnonymous${
						filters.searchQueryString ? "?searchQueryFilter=" + filters.searchQueryString : ""
					}${filters.paginationFrom ? "&from=" + filters.paginationFrom : ""}`,
				};
			},
		}),
		
		fetchDesignItem: builder.query<ConDesignItemWithPicture, string>({
			query(itemId) {
				return `/Home/GetDesignItemWithPicture?designItemId=${itemId}`;
			},
			providesTags: (result, error, designItemId) => [
				{ type: "DesignItem", id: designItemId },
				{ type: "Bookmark", id: designItemId },
			],
		}),
		
		fetchUserInformations: builder.query<UserInformations, void>({
			query() {
				return `/Home/GetUserInformations`;
			},
		}),
		
		fetchUser: builder.query<User, boolean>({
			query() {
				return `/User/GetUser`;
			},
		}),
		
		fetchCultureNames: builder.query<string[], void>({
			query() {
				return `/Home/GetCultureNames`;
			},
		}),
		
		fetchTranslations: builder.query<TranslationsRecord, string>({
			query(cultureName) {
				return `/Home/GetTranslation?cultureName=${cultureName}`;
			},
		}),
		
		fetchDetailScene: builder.query<SceneUrl, string>({
			query(itemId) {
				return `/Home/GetScene3dUrl/?designItemId=${itemId}`;
			},
		}),
		
		fetchDetailScenePost: builder.mutation<Scene, string>({
			query(itemId) {
				return {
					url: `/Home/GetScene`,
					method: "POST",
					body: { itemId },
				};
			},
			invalidatesTags: ["Post"],
		}),
		
		fetchScene: builder.mutation<Scene, MemberFilter[]>({
			query(members) {
				return {
					url: `/Connection/GetScene`,
					method: "POST",
					body: { members },
					headers: {
						"Content-Type": "application/json;charset=UTF-8",
						"Accept": "application/json",
					},
				};
			},
			invalidatesTags: ["Post"],
		}),
	}),
});

export const {
	useFetchSeqDataQuery,
	useFetchDesignItemQuery,
	useFetchDesignItemsLiteAnonymousQuery,
	useFetchUserInformationsQuery,
	useFetchUserQuery,
	useFetchSceneMutation,
	useFetchDetailSceneQuery,
	useLazyFetchIdeaConPublicUrlQuery,
	useBookmarkDesignItemMutation,
	useUnbookmarkDesignItemMutation,
} = clApiSlice;